// 全サーバー共通の IP BAN。edge でゲームの通信（Java 版・統合版・追加ポート）を落とす。
import { useState, type FormEvent } from 'react';
import { NetworkBanAddressSchema } from '@mcpanel/core';
import { apiM } from '../api/client-maint';
import { errorMessage, useApp } from '../app-context';
import { LoadingContent, Spinner } from '../components/feedback';
import { Empty, ErrorAlert } from '../components/ui';
import { FieldError } from '../components/FieldError';
import { formatDateTime } from '../format';
import { usePoll } from '../hooks/usePoll';

export function NetworkBansSection() {
  const { canOperate, notify } = useApp();
  const bans = usePoll(() => (canOperate ? apiM.networkBans() : Promise.resolve([])), 60000, [canOperate]);
  const [address, setAddress] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!canOperate) return null;
  const parsed = NetworkBanAddressSchema.safeParse(address);
  const act = async (operation: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await operation();
      await bans.reload();
      notify(message);
      return true;
    } catch (error) {
      notify(errorMessage(error), true);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const add = async (event: FormEvent) => {
    event.preventDefault();
    if (!parsed.success) return;
    if (
      await act(() => apiM.addNetworkBan(parsed.data, reason.trim() || undefined), `${parsed.data} を BAN しました`)
    ) {
      setAddress('');
      setReason('');
    }
  };
  return (
    <section className="card stack">
      <h2 data-search-id="network-ip-bans">IP BAN（全サーバー共通）</h2>
      <p className="small muted">
        入口（edge）で、Java 版・統合版・追加ポート（ボイスチャットなど）の通信を落とします。すべてのサーバーに効き、
        入っている人も切れます。パネルの Web と SFTP は落としません。同じ回線（学校・携帯の共有の
        IP）の人も入れなくなるので、 範囲での BAN は慎重に。サーバーごとの BAN
        は各サーバーの「プレイヤー」から行えます。
      </p>
      <form className="row" onSubmit={(event) => void add(event)}>
        <input
          className="input"
          style={{ flex: '1 1 220px' }}
          value={address}
          placeholder="IP（例: 203.0.113.5）か範囲（例: 203.0.113.0/24）"
          aria-label="BAN する IP"
          onChange={(event) => setAddress(event.target.value)}
        />
        <input
          className="input"
          style={{ flex: '1 1 220px' }}
          value={reason}
          maxLength={200}
          placeholder="理由（任意。パネルの中だけで見えます）"
          aria-label="理由"
          onChange={(event) => setReason(event.target.value)}
        />
        <button className="btn danger" disabled={busy || !parsed.success}>
          {busy && <Spinner />}
          BAN する
        </button>
      </form>
      {address && !parsed.success && (
        <FieldError id="network-ban-error" messages={[parsed.error.issues[0]?.message ?? '']} />
      )}
      <LoadingContent loading={!bans.data && bans.loading} shape="settings">
        <ErrorAlert error={bans.error} />
        {bans.data?.length === 0 && <Empty>BAN している IP はありません。</Empty>}
        {!!bans.data?.length && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>IP</th>
                  <th>理由</th>
                  <th>日時</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bans.data.map((ban) => (
                  <tr key={ban.address}>
                    <td>
                      <code>{ban.address}</code>
                    </td>
                    <td>{ban.reason ?? '—'}</td>
                    <td className="small">{formatDateTime(ban.createdAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn small"
                        disabled={busy}
                        onClick={() =>
                          void act(() => apiM.removeNetworkBan(ban.address), `${ban.address} の BAN を解除しました`)
                        }
                      >
                        解除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadingContent>
    </section>
  );
}
